import LaunchOutlined from "@mui/icons-material/LaunchOutlined";
import { Box } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { isPOBoxAddress } from "encamp-shared/src/utils/address";
import { gql } from "generated-graphql";
import {
  LepcPickerFragment,
  LepcSearchForPickerQuery,
  LepcsQuery,
} from "generated-graphql/graphql";
import { useValidatingFormContext } from "hooks/useValidatingForm";
import { forwardRef, useCallback, useMemo } from "react";
import { ControllerFieldState } from "react-hook-form";

import AsyncAutocomplete from "./Forms/AsyncAutocomplete";
import { DisplayOnlyField } from "./Forms/DisplayOnlyField";
import EncampAutocomplete from "./Forms/EncampAutocomplete";
import { calculateAgencyDistance, formatDistance, getLepcCoordinates } from "./LocalAgencies/utils";

// Base props shared between both picker variants
type BaseLepcPickerProps = {
  onChange?: (value: LepcPickerFragment | null) => void;
  value?: LepcPickerFragment | null;
  disabled?: boolean;
  helperText?: string | React.JSX.Element;
  displayAgencyLink?: boolean;
  facilityLatitude?: number | null;
  facilityLongitude?: number | null;
  /** Override the default "LEPC" label (e.g. "CUPA" for CA facilities) */
  label?: string;
} & ControllerFieldState;

// Props specific to state/county based picker
type StateCountyLepcPickerProps = BaseLepcPickerProps & {
  facilityState?: string;
  facilityCounty?: string;
};

// Props specific to facility based picker
type FacilityLepcPickerProps = BaseLepcPickerProps & {
  facilityIds: string[];
};

// Props specific to omnisearch picker with filtering
type OmnisearchLepcPickerProps = BaseLepcPickerProps & {
  defaultSearch?: string;
  resultFilter?: (lepc: LepcPickerFragment) => boolean;
};

// GraphQL fragments and queries
const LEPC_PICKER_FRAGMENT = gql(`
  fragment LepcPicker on Lepc {
    id
    name
    mailingAddress
    mailingAddressLine2
    contactName
    city
    state
    zip
    county
    phone
    email
    isEmailed
    isMailed
    isSubmittedViaPortal
    isStateSubmits
    needsT2sFile
    needsXmlZipFile
    preferUncertified
    deactivated
    latitude
    longitude
  }
`);

const LEPCS_BY_STATE_COUNTY_QUERY = gql(`
  query Lepcs($state: String, $county: String) {
    lepcs(state: $state, county: $county) {
      items {
        ...LepcPicker
      }
      count
    }
  }
`);

const LEPC_SEARCH_BY_FACILITY_QUERY = gql(`
  query LepcSearchForPicker($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!], $tenantId: String) {
    lepcSearch(search: $search, page: $page, pageSize: $pageSize, sort: $sort, tenantId: $tenantId) {
      items {
        ...LepcPicker
      }
      count
    }
  }
`);

// Shared function to format LEPC option labels
const getLepcOptionLabel = (lepc: LepcPickerFragment) => {
  const { name, city, county, state } = lepc ?? {};
  return [name, city, county, state].filter(Boolean).join(", ");
};

// Function to sort LEPCs by distance when coordinates are available
const sortLepcsByDistance = (
  lepcs: LepcPickerFragment[],
  facilityLatitude?: number | null,
  facilityLongitude?: number | null
): LepcPickerFragment[] => {
  if (!facilityLatitude || !facilityLongitude) {
    return lepcs;
  }

  const facilityCoords = {
    latitude: facilityLatitude,
    longitude: facilityLongitude,
  };

  // Calculate distances once for all LEPCs
  const lepcsWithDistances = lepcs.map((lepc) => ({
    lepc,
    distance: calculateAgencyDistance(facilityCoords, getLepcCoordinates(lepc)),
  }));

  return lepcsWithDistances
    .sort((a, b) => {
      const aDistance = a.distance;
      const bDistance = b.distance;

      // If both have distances, sort by distance
      if (aDistance !== undefined && bDistance !== undefined) {
        return aDistance - bDistance;
      }

      // If only one has distance, prefer the one WITHOUT distance (put at top)
      if (aDistance !== undefined && bDistance === undefined) {
        return 1;
      }
      if (aDistance === undefined && bDistance !== undefined) {
        return -1;
      }

      // If neither has distance, maintain original order
      return 0;
    })
    .map((item) => item.lepc);
};

// Custom render option function for displaying distance
const createLepcRenderOption = (
  facilityLatitude?: number | null,
  facilityLongitude?: number | null,
  displayAgencyLink?: boolean
) => {
  if (!facilityLatitude || !facilityLongitude) {
    if (!displayAgencyLink) return undefined;

    const LepcOptionWithLink = (
      props: React.HTMLAttributes<HTMLLIElement>,
      option: LepcPickerFragment
    ) => (
      <li
        {...props}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>{getLepcOptionLabel(option)}</span>
        {agencyLinkAdornment(option)}
      </li>
    );
    LepcOptionWithLink.displayName = "LepcOptionWithLink";
    return LepcOptionWithLink;
  }

  const facilityCoords = {
    latitude: facilityLatitude,
    longitude: facilityLongitude,
  };

  const LepcOptionWithDistance = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: LepcPickerFragment
  ) => {
    const agencyCoords = getLepcCoordinates(option);
    const distance = calculateAgencyDistance(facilityCoords, agencyCoords);
    const isPoBox = isPOBoxAddress(option.mailingAddress);

    return (
      <li {...props}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            gap: 1,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <span>{getLepcOptionLabel(option)}</span>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
            }}
          >
            {isPoBox ? (
              <Box
                sx={{
                  fontSize: "0.75rem",
                  color: "text.secondary",
                  whiteSpace: "nowrap",
                }}
              >
                PO Box
              </Box>
            ) : distance !== undefined ? (
              <Box
                sx={{
                  fontSize: "0.75rem",
                  color: "text.secondary",
                  whiteSpace: "nowrap",
                }}
              >
                {formatDistance(distance)}
              </Box>
            ) : null}
            {displayAgencyLink && agencyLinkAdornment(option)}
          </Box>
        </Box>
      </li>
    );
  };
  LepcOptionWithDistance.displayName = "LepcOptionWithDistance";
  return LepcOptionWithDistance;
};

// State/County based picker
// NOTE: Using forwardRef to properly handle ref forwarding and prevent React warnings
// when this component is used with form libraries like react-hook-form
export const LepcPicker = forwardRef<HTMLDivElement, StateCountyLepcPickerProps>(
  function LepcPicker(
    {
      facilityState,
      facilityCounty,
      onChange,
      value,
      error,
      disabled,
      helperText,
      displayAgencyLink,
      facilityLatitude,
      facilityLongitude,
      label = "LEPC",
    },
    ref
  ) {
    // Memoize the sorted items function
    const getSortedItems = useMemo(() => {
      return (data: LepcsQuery) => {
        const items = data.lepcs?.items ?? [];
        return sortLepcsByDistance(items, facilityLatitude, facilityLongitude);
      };
    }, [facilityLatitude, facilityLongitude]);

    // Memoize the render option function
    const renderOptionFunc = useMemo(() => {
      return createLepcRenderOption(facilityLatitude, facilityLongitude, displayAgencyLink);
    }, [facilityLatitude, facilityLongitude, displayAgencyLink]);

    const formContext = useValidatingFormContext();
    if (formContext?.metadata?.isDisplayOnly) {
      return <DisplayOnlyField text={value ? getLepcOptionLabel(value) : ""} label={label} />;
    }

    return (
      <EncampAutocomplete
        query={LEPCS_BY_STATE_COUNTY_QUERY}
        variables={{
          state: facilityState,
          county: facilityCounty,
        }}
        getItems={getSortedItems}
        getOptionLabel={getLepcOptionLabel}
        onChange={onChange}
        value={value || null}
        error={error}
        disabled={disabled}
        helperText={helperText}
        additionalEndAdornment={displayAgencyLink && agencyLinkAdornment(value)}
        renderOption={renderOptionFunc}
        label={label}
      />
    );
  }
);

// Omnisearch-based picker with state filtering and result filtering
// NOTE: Using forwardRef to properly handle ref forwarding and prevent React warnings
// when this component is used with form libraries like react-hook-form
export const LepcPickerOmnisearch = forwardRef<HTMLDivElement, OmnisearchLepcPickerProps>(
  function LepcPickerOmnisearch(
    {
      defaultSearch,
      resultFilter,
      onChange,
      value,
      error,
      disabled,
      helperText,
      displayAgencyLink,
      facilityLatitude,
      facilityLongitude,
      label = "LEPC",
    },
    ref
  ) {
    // Memoize the sorted items function
    const getSortedItems = useMemo(() => {
      return (data: LepcSearchForPickerQuery) => {
        const items = data.lepcSearch?.items ?? [];
        return sortLepcsByDistance(items, facilityLatitude, facilityLongitude);
      };
    }, [facilityLatitude, facilityLongitude]);

    // Memoize the render option function
    const renderOptionFunc = useMemo(() => {
      return createLepcRenderOption(facilityLatitude, facilityLongitude, displayAgencyLink);
    }, [facilityLatitude, facilityLongitude, displayAgencyLink]);

    const getItemsWithFilter = useCallback(
      (data: LepcSearchForPickerQuery) => {
        const items = getSortedItems(data);
        return resultFilter ? items.filter(resultFilter) : items;
      },
      [getSortedItems, resultFilter]
    );

    const formContext = useValidatingFormContext();
    if (formContext?.metadata?.isDisplayOnly) {
      return <DisplayOnlyField text={value ? getLepcOptionLabel(value) : ""} label={label} />;
    }

    return (
      <AsyncAutocomplete
        ref={ref}
        query={LEPC_SEARCH_BY_FACILITY_QUERY}
        getItems={getItemsWithFilter}
        getOptionLabel={getLepcOptionLabel}
        onChange={onChange}
        value={value ?? null}
        error={error}
        disabled={disabled}
        helperText={helperText}
        additionalSearchString={defaultSearch ?? ""}
        placeholderLabel={`Search ${label}s`}
        renderOption={renderOptionFunc}
        additionalEndAdornment={displayAgencyLink ? agencyLinkAdornment(value) : undefined}
      />
    );
  }
);

function agencyLinkAdornment(value: LepcPickerFragment | null | undefined) {
  return (
    value && (
      <Tooltip title="Open LEPC">
        <IconButton
          size="small"
          sx={{
            padding: "4px",
            color: "primary.main",
          }}
          onClick={(e) => {
            e.stopPropagation(); // Prevent triggering the dropdown
            window.open(`/staff/agencies/lepcs?omnisearch=id:${value.id}`, "_blank");
          }}
        >
          <LaunchOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
    )
  );
}
